import '../../sp-taxon-info/sp-taxon-info';

import {customElement, property, query, state} from 'lit/decorators.js';
import {css, html, LitElement, PropertyValues} from 'lit';
import {translate} from '@stefanholzapfel/lit-translate';
import {when} from 'lit/directives/when.js';
import {SpEmbedded} from "../../sp-dialog/sp-embedded";
import {SpDefaultDialog, SpDefaultDialogButtonConfig} from "../../sp-dialog/sp-default-dialog";
import {SpUiTaxon} from "./sp-taxon-input";
import {dispatchUpdateScrollbarsEvent, SpScrollbarController} from "../../../controllers/sp-scrollbar.controller";
import {perfectScrollbar} from "../../../styles/3rd-party/perfect-scrollbar.styles";
import {input} from "../../../styles/inputs/input.styles";
import {classMap} from "lit/directives/class-map.js";
import {SpUiConfigService} from "../../../services/sp-ui-config.service";
import {repeat} from "lit-html/directives/repeat.js";
import { live } from 'lit/directives/live.js';
import {translateUppercase} from "@stefanholzapfel/lit-translate/dist/translateUppercase.directive";
import {SpTaxonService} from "../../../services/sp-taxon.service";
import {unsafeCSSIcon} from "../../../styles/icons";

@customElement('sp-taxon-input-dialog')
export class SpTaxonInputDialog extends SpEmbedded<SpDefaultDialog>()(LitElement) {
    @property({type: Object})
    value: SpUiTaxon;
    @property({type: Function})
    changeCallback;
    @property({type: Function})
    queryChangeCallback;
    //TODO: provide or get from browser? (reactive?)
    @property({type: Boolean})
    offline = false;
    @property({ type: Array })
    taxonList: SpUiTaxon[] = [];
    @property({ type: Boolean })
    taxonListLoading = false;

    @state()
    query = null;
    @state()
    selectedTaxon: SpUiTaxon;

    @query('.select-list')
    selectList: HTMLInputElement;
    @query('input')
    searchInput: HTMLInputElement;
    scrollbarController = new SpScrollbarController(this);
    lastScrollposition = 0;

    static styles = [
        perfectScrollbar,
        //language=CSS
        css`
            :host {
                position: relative;
                height: 100%;
                display: block;
            }

            .select-list {
                list-style-type: none;
                overflow: hidden;
                position: relative;
                margin: 0;
                padding: 0;
            }

            .select-list li {
                position: relative;
                display: block;
                width: 100%;
                height: 50px;
                margin-bottom: 1px;
                box-sizing: border-box;
                background-color: #fff;
                border-left: 10px solid rgb(var(--sp-color-d));
                cursor: pointer;
            }

            .select-list li.active {
                background-color: rgb(var(--sp-color-b));
                border-color: rgb(var(--sp-color-b)) !important;
            }

            .select-list li.active span {
                color: white !important;
            }

            .select-list li:last-child {
                margin: 0;
            }

            .select-list li .option-label {
                display: block;
                width: 100%;
                height: 100%;
                font-size: 15px;
                line-height: 50px;
                box-sizing: border-box;
                padding: 0 10px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .select-list li .option-label.small {
                height: 50%;
                line-height: 20px;
                padding: 5px 10px 0 10px;
            }

            .select-list li .option-label.short {
                padding-right: 55px;
            }

            .select-list.multi li .option-label,
            .select-list.multi li .option-label.small {
                padding-left: 50px;
            }

            .select-list li .option-meta-label {
                display: block;
                width: 100%;
                height: 50%;
                font-size: 13px;
                line-height: 20px;
                box-sizing: border-box;
                padding: 0 10px 5px 10px;
                white-space: nowrap;
                overflow: hidden;
                box-sizing: border-box;
                color: #888;
            }

            .select-list.multi li .option-meta-label {
                padding-left: 50px;
            }

            .select-list li .option-meta-label.short {
                padding-right: 55px;
            }

            .select-list li.bg-color-b .option-meta-label {
                color: #fff;
            }

            .select-list li .option-info {
                display: block;
                position: absolute;
                top: 5px;
                right: 5px;
                width: 40px;
                height: 40px;
                background-size: cover !important;
                border-radius: 20px;
                background-repeat: no-repeat;
                background-position: center center;
            }

            .select-list li .option-info-icon {
                display: block;
                position: absolute;
                bottom: 5px;
                right: 5px;
                width: 16px;
                height: 16px;
                border-radius: 16px;
                background-size: auto 100%;
                background-repeat: no-repeat;
                background-position: center center;
                background-color: white;
                background-image: url(${unsafeCSSIcon('info-circle', { color: 'grey-dark', size: 'x128' })});
            }

            .select-list li .option-info.unlinked,
            .select-list li .option-info-icon.unlinked {
                cursor: default;
            }

            .taxon-list-container {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            .taxon-select-input {
                padding-bottom: calc(var(--sp-default-padding) / 3);
            }
        `,
        input
    ];

    afterShow = async (elem) => {
        this.searchInput.focus();
    }
    afterShowCallback = this.afterShow.bind(this);

    getDefaultButtons(): SpDefaultDialogButtonConfig[] {
        return [
            {
                text: translateUppercase('MODAL_CANCEL'),
                textPosition: 'center',
                clickCallback: () => {
                    this.cancelModal();
                }
            }
        ]
    }

    getInfoButtons(): SpDefaultDialogButtonConfig[] {
        return [
            {
                text: translateUppercase('MODAL_OK'),
                textPosition: 'center',
                clickCallback: () => {
                    this.select(this.selectedTaxon);
                }
            },
            {
                text: translateUppercase('MODAL_BACK'),
                textPosition: 'center',
                clickCallback: () => {
                    this.selectedTaxon = null;
                }
            }
        ]
    }

    render() {
        return html`
            ${ this.offline ?
                    html`<div>${translate('WARNING_OFFLINE')}</div>` :
                    this.selectedTaxon ? 
                            html `<sp-taxon-info
                                    .taxon=${this.selectedTaxon}></sp-taxon-info>` : 
                            html`
                                <div class="taxon-list-container">
                                    <div class="taxon-select-input">
                                        <input class="search-taxon" type="text" .value=${live(this.query ?? null)} @input=${ e => { this.searchQueryChanged(e.target.value) } }
                                               placeholder=${translate('TAXON_FIELD_SEARCH')}/>
                                    </div>
                                    <ul class="select-list">
                                        ${ repeat(this.taxonList ?? [], taxon => {
                                            return html`
                                                <li class=${classMap({ 'active': taxon.id === this.value?.id })}>
                                                    <span class=${ classMap({
                                                                    'option-label': true,
                                                                    small: true,
                                                                    short: taxon.image || taxon.wikipedia
                                                                })}
                                                          @click=${() => this.select(taxon)}>
                                                        ${taxon.label}${ when(taxon.label !== taxon.caption, () => ` (${taxon.caption})`) }
                                                    </span>
                                                    <span class=${ classMap({
                                                                    'option-meta-label': true,
                                                                    short: taxon.image || taxon.wikipedia
                                                                })}
                                                          @click=${() => this.select(taxon)}>
                                                        ${taxon.taxon_name}
                                                    </span>
                                                    ${ when(taxon.image || taxon.wikipedia, () => 
                                                        html`<span class="option-info"
                                                                    @click=${() => taxon.wikipedia ? this.selectedTaxon = taxon : this.select(taxon)}
                                                                    style="background-image:url('${taxon.image ? `${SpUiConfigService.get('SP_WIKIMEDIA_URI')}/${encodeURI(taxon.image)}?width=150px` : `${SpUiConfigService.get('SP_TOPIC_ASSET_HOST')}/img/dummy.jpg`}');"></span>`) }
                                                    ${ when(taxon.wikipedia, () =>
                                                        html`<span class="option-info-icon"
                                                                   @click=${() => { this.selectedTaxon = taxon } }>
                                                            </span>`)}
                                                </li>`;
                                        })}
                                        <li @click=${() => { this.select(SpTaxonService.getUnknownTaxon()) }}
                                            class=${classMap({
                                                'active': this.value?.id === 'unknown'
                                            })}>
                                            <span class="option-label">${translate('TAXON_FIELD_OPTION_UNKNOWN')}</span>
                                        </li>
                                        ${when(this.taxonListLoading, () => html`
                                            <sp-loading-more></sp-loading-more>`)}
                                    </ul>
                                </div>
                            `
            }`;
    }

    async searchQueryChanged(query) {
        this.query = query;
        if (!this.query || this.query === '') {
            this.taxonList = [];
        }
        this.queryChangeCallback(this.query);
    }

    select(taxon: SpUiTaxon) {
        this.changeCallback(taxon);
        this.dialog.hide();
    }

    cancelModal() {
        this.dialog.hide();
    }

    protected async firstUpdated(props: PropertyValues) {
        super.firstUpdated(props);
        if (this.query)
            this.queryChangeCallback(this.query);
        this.dialog.addEventListener('after-show', this.afterShowCallback);
        this.scrollbarController.add(this.selectList, {
            psOptions: {suppressScrollX: true},
            identifier: 'sp-taxon-input-dialog'
        });
        this.dialog.buttons = this.getDefaultButtons();
    }

    protected willUpdate(props: PropertyValues) {
        super.willUpdate(props);
        if (props.has('selectedTaxon') && this.selectedTaxon) {
            this.lastScrollposition = this.selectList.scrollTop;
        }
        if (props.has('value') && this.value && this.value.id !== 'unknown' && !this.query) {
            this.searchQueryChanged(this.value?.label);
        }
    }

    protected async updated(props) {
        super.updated(props);
        if (props.has('selectedTaxon') ) {
            if (this.selectedTaxon) {
                this.dialog.buttons = this.getInfoButtons();
            } else {
                this.scrollbarController.add(this.selectList, {
                    psOptions: {suppressScrollX: true},
                    identifier: 'sp-taxon-input-dialog'
                });
                dispatchUpdateScrollbarsEvent(this);
                this.selectList.scrollTop = this.lastScrollposition;
                this.searchInput.focus();
                this.dialog.buttons = this.getDefaultButtons();
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.dialog.removeEventListener('after-show', this.afterShowCallback);
    }
}
